<template>
  <div>
    <v-menu
      v-model="open"
      location="bottom end"
      :close-on-content-click="false"
      transition="scale-transition"
      offset="8"
    >
      <template #activator="{ props }">
        <v-btn icon variant="text" v-bind="props" class="notif-btn">
          <v-badge
            :content="unreadCount > 99 ? '99+' : unreadCount"
            :model-value="unreadCount > 0"
            color="red"
          >
            <v-icon size="22" color="#374151">mdi-bell-outline</v-icon>
          </v-badge>
        </v-btn>
      </template>

      <div class="notif-panel">
        <!-- Header -->
        <div class="notif-header">
          <span class="notif-title">Notifications</span>
          <span
            v-if="unreadCount > 0"
            class="notif-mark-all"
            @click="markAllRead"
          >Mark all read</span>
        </div>

        <v-divider />

        <!-- Loading -->
        <div v-if="loading && !notifications.length" class="notif-loading">
          <v-skeleton-loader
            v-for="n in 3"
            :key="n"
            type="list-item-two-line"
            class="px-2"
          />
        </div>

        <!-- Notification list -->
        <div v-else-if="notifications.length" class="notif-list">
          <div
            v-for="(notif, idx) in previewNotifications"
            :key="notif.id"
            class="notif-item"
            :class="{ 'notif-item--unread': !notif.isOpenned }"
            @click="openNotif(notif)"
          >
            <div class="notif-item-row">
              <v-icon size="18" :color="typeMeta(notif).color" class="mt-1">
                {{ typeMeta(notif).icon }}
              </v-icon>
              <div class="notif-item-content">
                <div class="notif-item-top">
                  <span class="notif-item-title">{{ notif.title }}</span>
                  <span class="notif-item-time">{{ timeAgo(notif.created_at) }}</span>
                </div>
                <div class="notif-item-body">{{ notif.msg }}</div>
              </div>
              <span v-if="!notif.isOpenned" class="notif-dot" />
            </div>
            <v-divider v-if="idx < previewNotifications.length - 1" class="mt-3" />
          </div>
        </div>

        <!-- Empty state -->
        <div v-else class="notif-empty">
          <v-icon size="36" color="#cbd5e1">mdi-bell-off-outline</v-icon>
          <div class="notif-empty-text">No notifications yet</div>
        </div>

        <!-- Footer -->
        <template v-if="notifications.length">
          <v-divider />
          <div class="notif-footer" @click="openAll">
            View All ({{ notifications.length }})
          </div>
        </template>
      </div>
    </v-menu>

    <!-- All notifications -->
    <v-dialog v-model="showAll" max-width="560" scrollable>
      <v-card rounded="xl">
        <v-card-title class="d-flex align-center pa-4">
          <span class="notif-title">All Notifications</span>
          <v-spacer />
          <span
            v-if="unreadCount > 0"
            class="notif-mark-all mr-3"
            @click="markAllRead"
          >Mark all read</span>
          <v-btn icon variant="text" size="small" @click="showAll = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider />
        <v-card-text class="pa-0" style="max-height: 70vh">
          <div
            v-for="(notif, idx) in notifications"
            :key="notif.id"
            class="notif-item"
            :class="{ 'notif-item--unread': !notif.isOpenned }"
            @click="openNotif(notif)"
          >
            <div class="notif-item-row">
              <v-icon size="18" :color="typeMeta(notif).color" class="mt-1">
                {{ typeMeta(notif).icon }}
              </v-icon>
              <div class="notif-item-content">
                <div class="notif-item-top">
                  <span class="notif-item-title">{{ notif.title }}</span>
                  <span class="notif-item-time">{{ timeAgo(notif.created_at) }}</span>
                </div>
                <div class="notif-item-body">{{ notif.msg }}</div>
              </div>
              <v-btn
                icon
                variant="text"
                size="x-small"
                title="Delete"
                @click.stop="deleteNotif(notif)"
              >
                <v-icon size="16" color="#94a3b8">mdi-trash-can-outline</v-icon>
              </v-btn>
            </div>
            <v-divider v-if="idx < notifications.length - 1" class="mt-3" />
          </div>
          <div v-if="!notifications.length" class="notif-empty">
            <v-icon size="36" color="#cbd5e1">mdi-bell-off-outline</v-icon>
            <div class="notif-empty-text">No notifications yet</div>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
const POLL_INTERVAL_MS = 30000;
const PREVIEW_LIMIT = 8;

const TYPE_META = {
  REQUIREMENT_SUBMITTED: { icon: "mdi-file-upload-outline", color: "#3b82f6" },
  REQUIREMENT_APPROVED: { icon: "mdi-check-circle-outline", color: "#10b981" },
  REQUIREMENT_DISAPPROVED: { icon: "mdi-alert-circle-outline", color: "#ef4444" },
  STUDENT_ENDORSED: { icon: "mdi-domain", color: "#8b5cf6" },
  STUDENT_DEPLOYED: { icon: "mdi-briefcase-check-outline", color: "#10b981" },
  STUDENT_PULLED_OUT: { icon: "mdi-account-arrow-left-outline", color: "#ef4444" },
  STUDENT_REASSIGNED: { icon: "mdi-swap-horizontal", color: "#f59e0b" },
  STUDENT_HTE_SWAPPING: { icon: "mdi-swap-horizontal", color: "#f59e0b" },
  STUDENT_EVALUATED: { icon: "mdi-clipboard-check-outline", color: "#3b82f6" },
  CORETIME_SUBMITTED: { icon: "mdi-clock-outline", color: "#3b82f6" },
  CORETIME_APPROVED: { icon: "mdi-clock-check-outline", color: "#10b981" },
  DEMERIT_ADDED: { icon: "mdi-alert-outline", color: "#ef4444" },
  INTERN_REQUEST_ADDED: { icon: "mdi-account-multiple-plus-outline", color: "#8b5cf6" },
  INTERN_REQUEST_GRANTED: { icon: "mdi-account-multiple-check-outline", color: "#10b981" },
  INTERN_FEEDBACK_ADDED: { icon: "mdi-message-text-outline", color: "#3b82f6" },
};
const DEFAULT_META = { icon: "mdi-bell-outline", color: "#64748b" };

export default {
  name: "NotificationDropdown",
  data: () => ({
    open: false,
    showAll: false,
    loading: false,
    notifications: [],
    serverUnreadCount: 0,
    pollTimer: null,
  }),
  computed: {
    unreadCount() {
      return this.notifications.filter((n) => !n.isOpenned).length;
    },
    previewNotifications() {
      return this.notifications.slice(0, PREVIEW_LIMIT);
    },
  },
  watch: {
    open(val) {
      if (val) this.fetchNotifications();
    },
  },
  created() {
    this.fetchNotifications();
  },
  mounted() {
    this.pollTimer = setInterval(this.poll, POLL_INTERVAL_MS);
    document.addEventListener("visibilitychange", this.poll);
  },
  beforeUnmount() {
    clearInterval(this.pollTimer);
    document.removeEventListener("visibilitychange", this.poll);
  },
  methods: {
    fetchNotifications() {
      this.loading = true;
      return this.axiosCall("/notifications/getMyNotifs", "GET")
        .then((res) => {
          if (res && Array.isArray(res.data)) {
            this.notifications = res.data;
            this.serverUnreadCount = this.unreadCount;
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },
    // Polls the cheap count endpoint and only reloads the list when it changed.
    poll() {
      if (document.hidden || !this.token) return;
      this.axiosCall("/notifications/getMyNewNotifsCount", "GET").then((res) => {
        if (res && typeof res.data === "number" && res.data !== this.serverUnreadCount) {
          this.fetchNotifications();
        }
      });
    },
    markAllRead() {
      this.axiosCall("/notifications/markAllAsRead", "POST").then((res) => {
        if (!res) return;
        this.notifications.forEach((n) => (n.isOpenned = 1));
        this.serverUnreadCount = 0;
      });
    },
    markRead(notif) {
      if (notif.isOpenned) return;
      notif.isOpenned = 1;
      this.serverUnreadCount = this.unreadCount;
      this.axiosCall("/notifications/markOneAsRead", "POST", { id: notif.id });
    },
    openNotif(notif) {
      this.markRead(notif);
      const path = this.resolveRoute(notif.redirect_route);
      if (!path) return;
      this.open = false;
      this.showAll = false;
      if (this.$route.path !== path) this.$router.push(path);
    },
    // redirect_route is a page slug relative to the viewer's role base path
    // (e.g. "my-requirements" → "/student/my-requirements").
    resolveRoute(slug) {
      if (!slug) return null;
      const base = this.$route.path.split("/")[1];
      if (!base) return null;
      const path = `/${base}/${slug}`;
      const resolved = this.$router.resolve(path);
      const isCatchAll = resolved.matched.some((r) => r.path.includes("pathMatch"));
      return resolved.matched.length && !isCatchAll ? path : null;
    },
    deleteNotif(notif) {
      this.axiosCall(`/notifications/${notif.id}`, "DELETE").then((res) => {
        if (!res) return;
        this.notifications = this.notifications.filter((n) => n.id !== notif.id);
        this.serverUnreadCount = this.unreadCount;
      });
    },
    openAll() {
      this.open = false;
      this.showAll = true;
    },
    typeMeta(notif) {
      return TYPE_META[notif.notification_type] || DEFAULT_META;
    },
    timeAgo(dateStr) {
      if (!dateStr) return "";
      const seconds = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
      if (seconds < 60) return "just now";
      const minutes = Math.floor(seconds / 60);
      if (minutes < 60) return `${minutes}m ago`;
      const hours = Math.floor(minutes / 60);
      if (hours < 24) return `${hours}h ago`;
      const days = Math.floor(hours / 24);
      if (days < 7) return `${days}d ago`;
      return new Date(dateStr).toLocaleDateString();
    },
  },
};
</script>

<style scoped>
.notif-btn {
  margin-right: 4px;
}

/* ── Panel ── */
.notif-panel {
  width: 360px;
  background: #ffffff;
  border: 1px solid #E2E8F0;
  border-radius: 12px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
  overflow: hidden;
}

.notif-list {
  max-height: 420px;
  overflow-y: auto;
}

/* ── Header ── */
.notif-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 18px 14px;
}

.notif-title {
  font-size: 16px;
  font-weight: 700;
  color: #111827;
}

.notif-mark-all {
  font-size: 13px;
  font-weight: 600;
  color: #10b981;
  cursor: pointer;
}

.notif-mark-all:hover {
  text-decoration: underline;
}

/* ── Items ── */
.notif-item {
  padding: 14px 18px;
  cursor: pointer;
  transition: background 0.12s;
}

.notif-item:hover {
  background: #f8fafc;
}

.notif-item--unread {
  background: #f0fdf4;
}

.notif-item--unread:hover {
  background: #dcfce7;
}

.notif-item-row {
  display: flex;
  align-items: flex-start;
  gap: 10px;
}

.notif-item-content {
  flex: 1;
  min-width: 0;
}

.notif-item-top {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 4px;
}

.notif-item-title {
  font-size: 14px;
  font-weight: 700;
  color: #111827;
  line-height: 1.3;
}

.notif-item-time {
  font-size: 12px;
  color: #94a3b8;
  white-space: nowrap;
  flex-shrink: 0;
}

.notif-item-body {
  font-size: 13px;
  color: #64748b;
  line-height: 1.5;
  word-break: break-word;
}

.notif-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #10b981;
  flex-shrink: 0;
  margin-top: 6px;
}

/* ── Loading ── */
.notif-loading {
  padding: 8px 0;
}

/* ── Empty ── */
.notif-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 32px 0;
  gap: 8px;
}

.notif-empty-text {
  font-size: 13px;
  color: #94a3b8;
}

/* ── Footer ── */
.notif-footer {
  padding: 13px;
  text-align: center;
  font-size: 14px;
  font-weight: 600;
  color: #64748b;
  cursor: pointer;
  transition: background 0.12s;
}

.notif-footer:hover {
  background: #f8fafc;
  color: #374151;
}
</style>
